import React, { useEffect, useState } from 'react';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import { FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@material-ui/core';
import AddStageInfo from './auxiliary/AddStageInfo2.component';
import CalendarPicker from './auxiliary/CalendarPicker.component';
import { AccountTypeEnum, accountTypes, getAccountTypeLabel } from '../../models/user.types';
import { ButtonOne } from '../ButtonOne/ButtonOne.component';
import { useStyles, styles } from './AddCommittee.styles';
import usePermission, { UserPermission } from '../../hooks/usePermission';
import { userOkeId } from '../../services/users/identity/user.service';
import useSessionUser from '../../hooks/useSessionUser';
import { useTranslation } from 'react-i18next';
import { Committee, committeesArray, getCommitteeLabel } from '../../models/committees.types';
import { getLocalISODate } from '../../utils/dateFunctionUtils';
import CloseButton from '../buttons/CloseButtton.component';

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, ...other } = props;
  return (
    <MuiDialogTitle className={classes.root} {...other}>
      {children}
    </MuiDialogTitle>
  );
});

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

const CancelButton = withStyles(() => ({
  root: {
    width: '135px',
    padding: '0px',
    height: '36px',
    left: '59px',
    top: '7px',
    border: 'solid 1px #3C419A',
    color: '#3C419A',
    backgroundColor: '#FFFFFF',
    marginBottom: '35px',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#FFFFFF',
      boxShadow: '0px 3px 1px 0px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 1px rgba(0,0,0,0.12)',
    },
  },
}))(ButtonOne);

const NextButton = withStyles(() => ({
  root: {
    position: 'relative',
    marginBottom: '35px',
    top: '7px',
    width: '135px',
    padding: '0px 0px 0px 0px',
    height: '36px',
    left: '39px',
    border: 'solid 1px #3C419A',
    color: '#FFFFFF',
    backgroundColor: '#3C419A',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#3C419A',
      boxShadow: '0px 3px 1px 0px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 1px rgba(0,0,0,0.12)',
    },
    '&:disabled': {
      backgroundColor: '#2C2D3E4D',
      color: '#FFFFFF',
      border: 'none',
    },
  },
}))(ButtonOne);

function getAccountType() {
  return accountTypes;
}

interface ICommiteeDataProps {
  isExaminerReadOnly: boolean;
  commiteeArg: string;
  accTypeArg: AccountTypeEnum | null;
  expDateArg?: Date | null;
  closeArgm: boolean;
  toStage3: boolean;
  commiteeDataToParent: (
    commitee: string,
    accType: AccountTypeEnum | null,
    expDate: Date | null,
    next: boolean,
    close: boolean,
    previous: boolean,
  ) => void;
}

const todayDate = new Date(getLocalISODate(new Date()));
todayDate.setUTCHours(0, 0, 0);

const AddCommiteeData: React.FC<ICommiteeDataProps> = ({ isExaminerReadOnly, commiteeDataToParent }) => {
  const classes = useStyles();
  const { user } = useSessionUser();
  const permission =
    usePermission(UserPermission.AdminOkeExaminerCreate) && !usePermission(UserPermission.AdminExaminerCreate);
  const commitees = permission ? [committeesArray[userOkeId(user) - 1]] : committeesArray;
  const { t } = useTranslation();
  const accountTypes = getAccountType();

  const [accType, setAccType] = useState<AccountTypeEnum | null>(isExaminerReadOnly ? AccountTypeEnum.Examiner : null);
  const [commitee, setCommitee] = useState<string>(permission ? userOkeId(user) : '');
  const [expDate, setExpDate] = useState<Date | null>(null);
  const [saveEnabled, setSaveEnabled] = useState<boolean>(false);

  const handleClose = () => {
    commiteeDataToParent('', null, null, false, true, false);
  };

  const handleNext = () => {
    commiteeDataToParent(commitee, accType, expDate, true, false, false);
  };

  const handleCommitee = (e: any) => {
    setCommitee(e);
  };

  const handleAccType = (e: any) => {
    setAccType(e);
  };

  const isDateValid = (_date: Date | null): boolean => {
    if (!_date) {
      return true;
    } else if (_date?.toString() === 'Invalid Date') {
      return false;
    } else if (_date < todayDate) {
      return false;
    } else {
      return true;
    }
  };

  const dateToParent = (_date: Date | null) => {
    let expirationDate = _date;
    if (expirationDate) {
      try {
        expirationDate = new Date(getLocalISODate(expirationDate));
      } catch {}
      expirationDate.setUTCHours(23, 59);
    }
    setExpDate(expirationDate);
  };

  const backToPreviousStage = () => {
    commiteeDataToParent('', null, new Date(), false, false, true);
  };

  useEffect(() => {
    if (commitee && accType && isDateValid(expDate)) {
      setSaveEnabled(true);
    } else {
      setSaveEnabled(false);
    }
  }, [commitee, accType, expDate]);

  return (
    <div>
      <DialogTitle id="dodaj użytkownika" onClose={handleClose}>
        <Grid container>
          <Grid item xs={6}>
            <div className={classes.modalTitle}>Dodaj użytkownika</div>
          </Grid>
          <Grid item xs={6}>
            <CloseButton onClick={handleClose} />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12}>
            <AddStageInfo backToPreviousStage={backToPreviousStage} />
          </Grid>
          <Grid item xs={12}>
            {commitees.length === 1 ? (
              <TextField
                disabled
                value={commitees[0].name}
                className={classes.accTypeInput}
                id="komisja"
                label="Komisja"
                aria-labelledby="komisja"
                placeholder="Komisja*"
                variant="outlined"
                inputProps={{
                  readOnly: true,
                  style: {
                    padding: '15.5px',
                  },
                }}
              />
            ) : (
              <FormControl variant="outlined" className={classes.commiteeInput}>
                <InputLabel id="demo-simple-select-outlined-label">Komisja*</InputLabel>
                <Select
                  value={commitee ?? ''}
                  onChange={(e) => handleCommitee(e.target.value)}
                  id="komisja"
                  label="Komisja*"
                  aria-labelledby="komisja"
                  SelectDisplayProps={{
                    style: {
                      padding: '15.5px',
                    },
                  }}
                >
                  {commitees.map((item: { name: string; id: Committee }) => (
                    <MenuItem
                      disableRipple
                      className={classes.menuItem}
                      key={item.id}
                      value={item.id}
                      disabled={
                        accType === AccountTypeEnum.ExamCommitteeEmployee &&
                        item.name == getCommitteeLabel(Committee.CKE)
                      }
                    >
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={12}>
            {isExaminerReadOnly ? (
              <TextField
                disabled
                value="Egzaminator"
                className={classes.accTypeInput}
                id="typ konta"
                label="Typ konta"
                aria-labelledby="typ konta"
                placeholder="Typ konta"
                variant="outlined"
                inputProps={{
                  readOnly: true,
                  style: {
                    padding: '15.5px',
                  },
                }}
              />
            ) : (
              <FormControl variant="outlined" className={classes.accTypeInput}>
                <InputLabel id="demo-simple-select-outlined-label">Typ konta*</InputLabel>
                <Select
                  name={getAccountTypeLabel(accType) ?? ''}
                  value={accType ?? ''}
                  onChange={(e) => handleAccType(e.target.value)}
                  id="typ konta"
                  label="Typ konta*"
                  aria-labelledby="typ konta"
                  SelectDisplayProps={{
                    style: {
                      padding: '15.5px',
                    },
                  }}
                >
                  {accountTypes.map((name, index) => (
                    <MenuItem
                      disableRipple
                      className={classes.menuItem}
                      key={index}
                      value={index + 1}
                      disabled={
                        name === getAccountTypeLabel(AccountTypeEnum.ExamCommitteeEmployee) &&
                        commitee == Committee.CKE.toString()
                      }
                    >
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={12}>
            <div className={classes.dateInput}>
              <CalendarPicker dateArg={expDate ?? new Date()} dateToParent={dateToParent}></CalendarPicker>
            </div>
          </Grid>
          <Grid item xs={6}>
            <CancelButton onClick={handleClose}>Anuluj</CancelButton>
          </Grid>
          <Grid item xs={6}>
            <NextButton onClick={handleNext} disabled={!saveEnabled}>
              Zapisz
            </NextButton>
          </Grid>
        </Grid>
      </DialogTitle>
    </div>
  );
};

export default AddCommiteeData;
